import {
    Alert,
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    CssBaseline,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Toolbar,
    styled,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../modal/Modal";
import { axiosClient } from "../AxiosClient";
import { formatCurrency } from "../contabilidadSiigo/siigoHelpers";

const PAGE_SIZE = 20;

const CSV_HEADERS =
    "COMMERCE_UUID,MES,ANIO,CREDITOS_OBJETIVO_1,CREDITOS_OBJETIVO_2,CREDITOS_OBJETIVO_3,VALOR_INCENTIVO_OBJETIVO_1,VALOR_INCENTIVO_OBJETIVO_2,VALOR_INCENTIVO_OBJETIVO_3";

const MONTHS = [
    { value: 1, label: "Enero" },
    { value: 2, label: "Febrero" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Mayo" },
    { value: 6, label: "Junio" },
    { value: 7, label: "Julio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" },
    { value: 11, label: "Noviembre" },
    { value: 12, label: "Diciembre" },
];

const TITLE_COLUMNS = [
    "Comercio",
    "Mes",
    "Año",
    "Créditos objetivo 1",
    "Créditos objetivo 2",
    "Créditos objetivo 3",
    "Valor incentivo 1",
    "Valor incentivo 2",
    "Valor incentivo 3",
];

const INITIAL_FORM = {
    commerce: null,
    mes: "",
    anio: "",
    creditosObjetivo1: "",
    creditosObjetivo2: "",
    creditosObjetivo3: "",
    valorIncentivoObjetivo1: "",
    valorIncentivoObjetivo2: "",
    valorIncentivoObjetivo3: "",
};

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
}));

const ButtonPrincipal = styled(Button)({
    flexGrow: 0,
    borderRadius: "20px",
    gap: "10px",
    backgroundColor: "#161e55",
    color: "#ffff",
    margin: "0 0.5rem",
    "&:hover": {
        backgroundColor: "#A8C8AF",
    },
});

const ButtonFile = styled(Button)({
    flexGrow: 0,
    borderRadius: "20px",
    gap: "10px",
    backgroundColor: "#ffff",
    color: "#161e55",
    margin: "0 0.5rem",
    border: "1px solid rgba(11, 10, 10, 0.17)",
    "&:hover": {
        backgroundColor: "#A8C8AF",
    },
});

const TextMessage = styled(FormHelperText)({
    color: "red",
});

const getMonthName = (mes) => MONTHS.find((month) => month.value === mes)?.label ?? mes ?? "—";

const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

const downloadIncentiveTemplate = () => {
    const blob = new Blob([`${CSV_HEADERS}\n`], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "plantilla_incentivos.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
};

const parsePositiveInteger = (value) => {
    if (value === "" || value === null || value === undefined) {
        return null;
    }
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return null;
    }
    return parsed;
};

const parsePositiveNumber = (value) => {
    if (value === "" || value === null || value === undefined) {
        return null;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed) || parsed <= 0) {
        return null;
    }
    return parsed;
};

const validateCreateForm = (form) => {
    const errors = {};

    if (!form.commerce?.uuid) {
        errors.commerce = "Seleccione un comercio";
    }

    const mes = parsePositiveInteger(form.mes);
    if (!mes || mes < 1 || mes > 12) {
        errors.mes = "Seleccione un mes válido";
    }

    const anio = parsePositiveInteger(form.anio);
    if (!anio) {
        errors.anio = "Ingrese un año válido mayor a 0";
    }

    ["creditosObjetivo1", "creditosObjetivo2", "creditosObjetivo3"].forEach((field) => {
        if (!parsePositiveInteger(form[field])) {
            errors[field] = "Ingrese un entero mayor a 0";
        }
    });

    ["valorIncentivoObjetivo1", "valorIncentivoObjetivo2", "valorIncentivoObjetivo3"].forEach((field) => {
        if (!parsePositiveNumber(form[field])) {
            errors[field] = "Ingrese un valor mayor a 0";
        }
    });

    return errors;
};

const buildGoalsQuery = (page) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("size", PAGE_SIZE);
    return params.toString();
};

export default function Incentivos() {
    const navigate = useNavigate();
    const importerDocument = useRef(null);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [commerces, setCommerces] = useState([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [form, setForm] = useState(INITIAL_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [importFileSelected, setImportFileSelected] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");

    const showSnackbar = (message, severity = "info") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const fetchGoals = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosClient.get(
                `/v1/advisor-incentive-goals?${buildGoalsQuery(page)}`
            );
            const data = response?.data;
            setRows(data?.content || []);
            setTotalElements(data?.totalElements ?? 0);
        } catch (error) {
            showSnackbar(
                getErrorMessage(error, "No fue posible cargar los incentivos."),
                "error"
            );
            setRows([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        if (localStorage.getItem("userUuid") == null) {
            window.location = "/admin";
            return;
        }
        fetchGoals();
    }, [fetchGoals]);

    useEffect(() => {
        axiosClient
            .get("/v1/commerces/all-commerces")
            .then((response) => {
                const sorted = (response.data || []).sort((a, b) => a.oldId - b.oldId);
                setCommerces(sorted);
            })
            .catch(() => {
                showSnackbar("No fue posible cargar la lista de comercios.", "error");
            });
    }, []);

    const handleOpenCreateModal = () => {
        setForm(INITIAL_FORM);
        setFormErrors({});
        setCreateModalOpen(true);
    };

    const handleCloseCreateModal = () => {
        setCreateModalOpen(false);
        setForm(INITIAL_FORM);
        setFormErrors({});
    };

    const handleOpenImportModal = () => {
        setImportFileSelected(false);
        if (importerDocument.current) {
            importerDocument.current.value = "";
        }
        setImportModalOpen(true);
    };

    const handleCloseImportModal = () => {
        setImportModalOpen(false);
        setImportFileSelected(false);
        if (importerDocument.current) {
            importerDocument.current.value = "";
        }
    };

    const handleFormChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleCreateSubmit = async (event) => {
        event.preventDefault();
        const errors = validateCreateForm(form);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setSubmitting(true);
        try {
            await axiosClient.post("/v1/advisor-incentive-goals", {
                commerceUuid: form.commerce.uuid,
                mes: parsePositiveInteger(form.mes),
                anio: parsePositiveInteger(form.anio),
                creditosObjetivo1: parsePositiveInteger(form.creditosObjetivo1),
                creditosObjetivo2: parsePositiveInteger(form.creditosObjetivo2),
                creditosObjetivo3: parsePositiveInteger(form.creditosObjetivo3),
                valorIncentivoObjetivo1: parsePositiveNumber(form.valorIncentivoObjetivo1),
                valorIncentivoObjetivo2: parsePositiveNumber(form.valorIncentivoObjetivo2),
                valorIncentivoObjetivo3: parsePositiveNumber(form.valorIncentivoObjetivo3),
            });
            showSnackbar("Incentivo creado correctamente.", "success");
            handleCloseCreateModal();
            await fetchGoals();
        } catch (error) {
            showSnackbar(
                getErrorMessage(error, "No fue posible crear el incentivo."),
                "error"
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleImporterChange = () => {
        setImportFileSelected(Boolean(importerDocument.current?.files?.length));
    };

    const handleImportSubmit = async () => {
        if (!importerDocument.current?.files?.length) {
            showSnackbar("Seleccione un archivo CSV.", "error");
            return;
        }

        const formData = new FormData();
        formData.append("file", importerDocument.current.files[0]);

        setSubmitting(true);
        try {
            await axiosClient.post("/v1/advisor-incentive-goals/import", formData);
            showSnackbar("Incentivos importados correctamente.", "success");
            handleCloseImportModal();
            await fetchGoals();
        } catch (error) {
            showSnackbar(
                getErrorMessage(error, "No fue posible importar el archivo."),
                "error"
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    return (
        <>
            <CssBaseline />
            <Toolbar />
            <Box sx={{ width: "100%" }}>
                <Stack spacing={2}>
                    <Item>.</Item>
                    <Item />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={1}
                            >
                                <h1>Incentivos</h1>
                                <ButtonPrincipal onClick={() => navigate("/homeAdmin/comercios")}>
                                    Volver a Comercios
                                </ButtonPrincipal>
                                <ButtonPrincipal onClick={handleOpenCreateModal}>
                                    Crear
                                </ButtonPrincipal>
                                <ButtonPrincipal onClick={handleOpenImportModal}>
                                    Importar
                                </ButtonPrincipal>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="tabla incentivos">
                                <TableHead>
                                    <TableRow>
                                        {TITLE_COLUMNS.map((title) => (
                                            <TableCell key={title} align="center">
                                                {title}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell align="center" colSpan={TITLE_COLUMNS.length}>
                                                Cargando contenido
                                            </TableCell>
                                        </TableRow>
                                    ) : rows.length > 0 ? (
                                        rows.map((row) => (
                                            <TableRow
                                                key={row.uuid}
                                                sx={{
                                                    "&:last-child td, &:last-child th": { border: 0 },
                                                }}
                                            >
                                                <TableCell align="center">{row.commerce ?? "—"}</TableCell>
                                                <TableCell align="center">
                                                    {getMonthName(row.mes)}
                                                </TableCell>
                                                <TableCell align="center">{row.anio ?? "—"}</TableCell>
                                                <TableCell align="center">
                                                    {row.creditosObjetivo1 ?? "—"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {row.creditosObjetivo2 ?? "—"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {row.creditosObjetivo3 ?? "—"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {formatCurrency(row.valorIncentivoObjetivo1)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {formatCurrency(row.valorIncentivoObjetivo2)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {formatCurrency(row.valorIncentivoObjetivo3)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell align="center" colSpan={TITLE_COLUMNS.length}>
                                                No hay incentivos registrados
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                rowsPerPageOptions={[PAGE_SIZE]}
                                count={totalElements}
                                rowsPerPage={PAGE_SIZE}
                                page={page}
                                onPageChange={handleChangePage}
                            />
                        </TableContainer>
                    </Grid>
                </Stack>
            </Box>
            <Toolbar />

            {createModalOpen && (
                <Modal activeModal={createModalOpen} handleModal={handleCloseCreateModal}>
                    <Box textAlign="center" px={3} pb={3}>
                        <h2>Crear incentivo</h2>
                    </Box>
                    <Box width="80%" margin="0 auto 2rem" px={2}>
                        <form onSubmit={handleCreateSubmit}>
                            <Box mt={2}>
                                <Autocomplete
                                    size="small"
                                    options={commerces}
                                    value={form.commerce}
                                    onChange={(_, newValue) => handleFormChange("commerce", newValue)}
                                    getOptionLabel={(option) =>
                                        option ? `${option.oldId}. ${option.name}` : ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option?.uuid === value?.uuid
                                    }
                                    filterOptions={(options, { inputValue }) => {
                                        const search = inputValue.toLowerCase();
                                        return options.filter(
                                            (commerce) =>
                                                String(commerce.oldId)
                                                    .toLowerCase()
                                                    .includes(search) ||
                                                (commerce.name &&
                                                    commerce.name.toLowerCase().includes(search))
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Comercio"
                                            error={Boolean(formErrors.commerce)}
                                            helperText={formErrors.commerce}
                                        />
                                    )}
                                    noOptionsText="Ningún comercio coincide"
                                />
                            </Box>
                            <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                                <FormControl fullWidth size="small" error={Boolean(formErrors.mes)}>
                                    <InputLabel id="mes-label">Mes</InputLabel>
                                    <Select
                                        labelId="mes-label"
                                        label="Mes"
                                        value={form.mes}
                                        onChange={(event) =>
                                            handleFormChange("mes", event.target.value)
                                        }
                                    >
                                        {MONTHS.map((month) => (
                                            <MenuItem key={month.value} value={month.value}>
                                                {month.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formErrors.mes && <TextMessage>{formErrors.mes}</TextMessage>}
                                </FormControl>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Año"
                                    type="number"
                                    value={form.anio}
                                    onChange={(event) =>
                                        handleFormChange("anio", event.target.value)
                                    }
                                    error={Boolean(formErrors.anio)}
                                    helperText={formErrors.anio}
                                />
                            </Box>
                            <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Créditos objetivo 1"
                                    type="number"
                                    value={form.creditosObjetivo1}
                                    onChange={(event) =>
                                        handleFormChange("creditosObjetivo1", event.target.value)
                                    }
                                    error={Boolean(formErrors.creditosObjetivo1)}
                                    helperText={formErrors.creditosObjetivo1}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Créditos objetivo 2"
                                    type="number"
                                    value={form.creditosObjetivo2}
                                    onChange={(event) =>
                                        handleFormChange("creditosObjetivo2", event.target.value)
                                    }
                                    error={Boolean(formErrors.creditosObjetivo2)}
                                    helperText={formErrors.creditosObjetivo2}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Créditos objetivo 3"
                                    type="number"
                                    value={form.creditosObjetivo3}
                                    onChange={(event) =>
                                        handleFormChange("creditosObjetivo3", event.target.value)
                                    }
                                    error={Boolean(formErrors.creditosObjetivo3)}
                                    helperText={formErrors.creditosObjetivo3}
                                />
                            </Box>
                            <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Valor incentivo 1"
                                    type="number"
                                    value={form.valorIncentivoObjetivo1}
                                    onChange={(event) =>
                                        handleFormChange(
                                            "valorIncentivoObjetivo1",
                                            event.target.value
                                        )
                                    }
                                    error={Boolean(formErrors.valorIncentivoObjetivo1)}
                                    helperText={formErrors.valorIncentivoObjetivo1}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Valor incentivo 2"
                                    type="number"
                                    value={form.valorIncentivoObjetivo2}
                                    onChange={(event) =>
                                        handleFormChange(
                                            "valorIncentivoObjetivo2",
                                            event.target.value
                                        )
                                    }
                                    error={Boolean(formErrors.valorIncentivoObjetivo2)}
                                    helperText={formErrors.valorIncentivoObjetivo2}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Valor incentivo 3"
                                    type="number"
                                    value={form.valorIncentivoObjetivo3}
                                    onChange={(event) =>
                                        handleFormChange(
                                            "valorIncentivoObjetivo3",
                                            event.target.value
                                        )
                                    }
                                    error={Boolean(formErrors.valorIncentivoObjetivo3)}
                                    helperText={formErrors.valorIncentivoObjetivo3}
                                />
                            </Box>
                            <Box mt={4} textAlign="center">
                                <ButtonPrincipal type="submit" disabled={submitting}>
                                    Guardar
                                </ButtonPrincipal>
                            </Box>
                        </form>
                    </Box>
                </Modal>
            )}

            {importModalOpen && (
                <Modal activeModal={importModalOpen} handleModal={handleCloseImportModal}>
                    <Box textAlign="center">
                        <h2>Importar incentivos</h2>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            flexDirection: "column",
                            height: "50%",
                            px: 3,
                        }}
                    >
                        <Box mt={3} mb={2}>
                            <ButtonFile onClick={downloadIncentiveTemplate}>
                                Descargar plantilla
                            </ButtonFile>
                        </Box>
                        <Box
                            mt={2}
                            mb={2}
                            display="flex"
                            alignItems="center"
                            border="1px solid gray"
                            padding="1rem"
                            borderRadius="1rem"
                            gap={2}
                            flexWrap="wrap"
                            justifyContent="center"
                        >
                            <InputLabel id="incentive-csv">Archivo CSV</InputLabel>
                            <ButtonFile onClick={() => importerDocument.current?.click()}>
                                <input
                                    accept=".csv"
                                    type="file"
                                    hidden
                                    ref={importerDocument}
                                    onChange={handleImporterChange}
                                />
                                Subir documento
                            </ButtonFile>
                        </Box>
                        {importFileSelected && <p>Documento cargado</p>}
                        <Box sx={{ display: "flex", justifyContent: "center", gap: "1rem", mt: 2 }}>
                            <ButtonPrincipal onClick={handleImportSubmit} disabled={submitting}>
                                Cargar
                            </ButtonPrincipal>
                        </Box>
                    </Box>
                </Modal>
            )}

            <Backdrop
                open={loading || submitting}
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}
